/* FRK image anti-copy deterrent (reusable module; see docs/IMAGE-ANTI-COPY.md).
   Discourages right-click "Save image as", dragging a photo to the desktop and (Android) the long-press save menu.
   It is a deterrent only: it does not stop screenshots, developer tools, browser extensions or direct image URLs.
   One delegated listener per event on <document>, so cards re-rendered by AJAX filtering, gallery clones, mega-menu
   panels and PhotoSwipe images are covered with no observers and no per-image work. The contextmenu listener runs in
   the capture phase so a menu that stops propagation cannot hide an image from it. Clicks, taps, swipes and pinch
   are never touched, and nothing is cancelled except the browser's own image menu / image drag. */
(() => {
  'use strict';
  if (window.__frkImageAntiCopy) return;
  window.__frkImageAntiCopy = true;

  // Never protected: the logo, footer, third-party widgets and embeds, anything marked data-frk-allow-image
  // (on the image or an ancestor). The header and its mega menus are NOT excluded: their photos are protected.
  const EXCLUDE = 'footer, .grp-footer, .grp-logo, .frk-logo, iframe, [data-frk-allow-image], [class*="jdgm"], [id*="tawk"], [class*="klaviyo"], [class*="kl-"]';
  const MIN_W = 80;   // icons, badges and small logos are left alone...
  const MIN_H = 60;
  // ...except in these areas, where every picture counts (brand logos are only ~25-40px tall).
  // MEGA is also a no-context-menu zone: a native menu popping up there freezes the hover navigation between panels.
  const MEGA = '.grp-mega';
  const ALWAYS = '.grp-mega, .frk-bstrip';
  // Elements that should keep the normal browser menu even when they sit over a photo
  const INTERACTIVE = 'a, button, input, select, textarea, label, summary, video, audio, canvas, svg, iframe, embed, object, [role="button"], [contenteditable]';

  function protectedImage(img) {
    if (!img || img.tagName !== 'IMG' || img.closest(EXCLUDE)) return false;
    if (img.closest(ALWAYS)) return true;
    const box = img.getBoundingClientRect();
    return box.width >= MIN_W && box.height >= MIN_H;
  }

  // True when the point is over actual text of this element (so text keeps its normal menu)
  function onText(el, x, y) {
    for (const node of el.childNodes) {
      if (node.nodeType !== 3 || !node.textContent.trim()) continue;
      const range = document.createRange();
      range.selectNodeContents(node);
      for (const r of range.getClientRects()) if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return true;
    }
    return false;
  }

  // A see-through layout wrapper (no fill, or just a tint/gradient) that has no text under the pointer
  function seeThrough(el, x, y) {
    if (el.matches(INTERACTIVE) || onText(el, x, y)) return false;
    const cs = getComputedStyle(el);
    if (/url\(/.test(cs.backgroundImage)) return false;
    const m = cs.backgroundColor.match(/[\d.]+/g);
    const alpha = m && m.length > 3 ? parseFloat(m[3]) : 1;
    return alpha < 0.95;
  }

  // The photo being right-clicked: the image itself, or an image directly behind see-through wrappers
  // (e.g. a hero photo under a text container). Opaque cards, links, buttons and text are never treated as photos.
  function photoAt(event) {
    const target = event.target;
    if (target && target.tagName === 'IMG') return protectedImage(target);
    if (!target || target.closest('footer, .grp-footer, iframe')) return false;
    const stack = document.elementsFromPoint(event.clientX, event.clientY);
    for (const el of stack) {
      if (el.tagName === 'IMG') return protectedImage(el);
      // A photo card: a link that wraps the photo, with the pointer on its picture area rather than its text
      if (el.tagName === 'A' && el.querySelector('img') && !onText(el, event.clientX, event.clientY)) continue;
      if (!seeThrough(el, event.clientX, event.clientY)) return false;
    }
    return false;
  }

  // A right-button press would focus the link under it, and the mega menu stays open while it holds focus
  // (:focus-within), which froze the hover navigation to the next panel. Keep focus where it was instead.
  document.addEventListener('mousedown', (event) => {
    if (event.button === 2 && event.target.closest && event.target.closest(MEGA)) event.preventDefault();
  }, true);

  document.addEventListener('contextmenu', (event) => {
    const t = event.target;
    // Inside the mega menu nothing offers a browser menu (text boxes aside), so it never gets stuck open on a right-click
    if (t && t.closest && t.closest(MEGA) && !t.closest('input, textarea, select, [contenteditable]')) { event.preventDefault(); return; }
    if (photoAt(event)) event.preventDefault();
  }, true);

  // Dragging a photo out of the page. A click on the surrounding link still works.
  document.addEventListener('dragstart', (event) => {
    if (protectedImage(event.target)) event.preventDefault();
  });
})();
